import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ClassForm = ({ 
  open = false,
  onClose,
  initialData = {}, 
  onSubmit, 
  loading = false,
  teachers = [], // Array of teachers for head teacher selection
  teachersError = null, // Error message for teachers loading or API errors
  title = 'Add New Class',
  onErrorClear = null // Callback to clear error in parent
}) => {
  const [formData, setFormData] = useState({
    name: '',
    section: '',
    order_number: '',
    head_teacher_id: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        name: initialData.name || '',
        section: initialData.section || '',
        order_number: initialData.order_number ?? '',
        head_teacher_id: initialData.head_teacher_id || '',
      });
    } else {
      // Reset form when creating new class
      setFormData({
        name: '',
        section: '',
        order_number: '',
        head_teacher_id: '',
      });
    }
    // Clear errors when form opens/closes
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing/changing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
    
    // Clear API error when user changes head teacher
    if (field === 'head_teacher_id' && teachersError && onErrorClear) {
      onErrorClear();
    }
  };

  const handleTeacherChange = (event, newValue) => {
    const teacherId = newValue ? newValue.id : '';
    setFormData(prev => ({
      ...prev,
      head_teacher_id: teacherId,
    }));

    // Clear error when user changes teacher
    if (errors.head_teacher_id) {
      setErrors(prev => ({
        ...prev,
        head_teacher_id: '',
      }));
    }
    
    // Clear API error when user changes head teacher
    if (teachersError && onErrorClear) {
      onErrorClear();
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Class name is required';
    }

    if (!formData.section.trim()) {
      newErrors.section = 'Section is required';
    }
    if (formData.order_number === '' || formData.order_number === null) {
      newErrors.order_number = 'Order number is required';
    } else if (Number.isNaN(Number(formData.order_number))) {
      newErrors.order_number = 'Order number must be numeric';
    } else if (Number(formData.order_number) < 0) {
      newErrors.order_number = 'Order number must be zero or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(() => {
    if (validateForm()) {
      onSubmit(formData);
    }
  }, [validateForm, onSubmit, formData]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 300,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Fill in the details to {initialData?.id ? 'update' : 'create'} a class
          </Typography>

          <TextField
            label="Class Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            disabled={loading}
            placeholder="e.g., Class 10A, Grade 5B"
          />

          <TextField
            label="Section"
            value={formData.section}
            onChange={handleChange('section')}
            error={!!errors.section}
            helperText={errors.section}
            fullWidth
            required
            disabled={loading}
            placeholder="e.g., A, B, C"
          />

          <TextField
            label="Order Number"
            type="number"
            value={formData.order_number}
            onChange={handleChange('order_number')}
            error={!!errors.order_number}
            helperText={errors.order_number || 'Used to determine promotion sequence'}
            fullWidth
            required
            disabled={loading}
            inputProps={{ min: 0 }}
          />

          <FormControl fullWidth error={!!errors.head_teacher_id || !!teachersError}>
            <Autocomplete
              options={teachers}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.full_name || option.name || `${option.first_name || ''} ${option.last_name || ''}`.trim() || 'Unknown Teacher';
              }}
              value={teachers.find((teacher) => teacher.id === formData.head_teacher_id) || null}
              onChange={handleTeacherChange}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Head Teacher"
                  placeholder="Search and select a teacher..."
                  error={!!errors.head_teacher_id || !!teachersError}
                  helperText={errors.head_teacher_id || teachersError}
                />
              )}
              noOptionsText="No teachers found"
              ListboxProps={{
                style: {
                  maxHeight: 200,
                },
              }}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Saving...' : (initialData?.id ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClassForm;
